/**
 * Hook to subscribe to specific ChartStore events and trigger re-renders.
 * Enables selective updates - only components that need specific data re-render.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getChartLogger } from "~utils/ticker-chart";
import type { ChartStore, ChartStoreEvent } from "~types/types";

/**
 * Subscribe to one or more ChartStore events.
 * Forces component re-render when subscribed events fire.
 *
 * @param store - ChartStore instance
 * @param events - Single event or array of events to subscribe to
 * @returns Current revision number (increments on each subscribed event)
 *
 * @example
 * ```tsx
 * // Subscribe to hover events only
 * const Crosshair = ({ store }) => {
 *   useChartSubscription(store, 'hover');
 *   const hoverState = store.getHoverState();
 *   // Component re-renders only when hover changes
 * };
 * ```
 */
export function useChartSubscription(
  store: ChartStore,
  events: ChartStoreEvent | ChartStoreEvent[]
): number {
  const [revision, setRevision] = useState(0);

  // Ref for logging which events triggered the update
  const eventsRef = useRef(events);

  eventsRef.current = events;

  const forceUpdate = useCallback(() => {
    const logger = getChartLogger();
    const eventStr = Array.isArray(eventsRef.current)
      ? eventsRef.current.join(",")
      : eventsRef.current;

    logger.debug(`forceUpdate for events: ${eventStr}`);
    setRevision((prev) => prev + 1);
  }, []);

  // Stabilize the events array to prevent infinite re-subscriptions
  const eventArray = useMemo(
    () => (Array.isArray(events) ? events : [events]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Array.isArray(events) ? events.join(",") : events]
  );

  // Subscribe to the current store instance
  // Re-subscribes when store changes (e.g., during HMR)
  useEffect(() => {
    const unsubscribe = store.subscribeMany(eventArray, forceUpdate);

    return unsubscribe;
  }, [store, eventArray, forceUpdate]);

  return revision;
}
